import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, Receipt, ClipboardList, Layers } from "lucide-react";
import { fetchTemplates } from "@/lib/api";

// Distinct visual mini-preview per base layout (matches the actual PDF aesthetic).
function TplPreview({ base, accent }) {
  const bar = "absolute h-1 rounded-sm";
  switch (base) {
    case "swiss":
      return (
        <div className="w-full h-full p-3 bg-white">
          <div className="text-[5px] font-mono uppercase tracking-widest">Tax Invoice</div>
          <div className="h-0.5 bg-black mt-1"></div>
          <div className="mt-2 text-[7px] font-bold">Company</div>
          <div className={`${bar} left-3 right-3 bg-gray-700`} style={{ top: "45%" }}></div>
          <div className={`${bar} left-3 right-3 bg-gray-200`} style={{ top: "52%" }}></div>
          <div className={`${bar} left-3 right-3 bg-gray-200`} style={{ top: "58%" }}></div>
          <div className="absolute bottom-3 right-3 text-[7px] font-bold" style={{ color: accent }}>TOTAL —</div>
        </div>
      );
    case "editorial":
      return (
        <div className="w-full h-full p-3" style={{ background: "#FDFCF9" }}>
          <div className="text-[9px] font-serif italic" style={{ color: accent }}>Tax Invoice</div>
          <div className="text-[5px] italic text-gray-500 mt-1">No. INV-0001</div>
          <div className="h-px bg-gray-300 my-2"></div>
          <div className={`${bar} left-3 right-3 bg-gray-200`} style={{ top: "60%" }}></div>
          <div className={`${bar} left-3 right-3 bg-gray-200`} style={{ top: "68%" }}></div>
          <div className="absolute bottom-3 right-3 text-[7px] font-serif font-bold" style={{ color: accent }}>Total —</div>
        </div>
      );
    case "brutalist":
      return (
        <div className="w-full h-full bg-white border-2 border-black p-2 font-mono">
          <div className="text-[5px] uppercase border-b border-black pb-1">// Tax Invoice</div>
          <div className="mt-1 text-[7px] font-bold uppercase">COMPANY</div>
          <div className="mt-3 h-4 flex items-center justify-between px-2" style={{ background: accent }}>
            <span className="text-white text-[5px] font-bold">TOTAL</span>
            <span className="text-white text-[5px] font-bold">₹—</span>
          </div>
        </div>
      );
    case "organic":
      return (
        <div className="w-full h-full bg-white p-4">
          <div className="text-[4px] text-gray-400 lowercase">tax invoice</div>
          <div className="mt-3 text-[8px] font-semibold tracking-tight">Company</div>
          <div className={`${bar} left-4 right-4 bg-gray-200`} style={{ top: "60%" }}></div>
          <div className={`${bar} left-4 right-4 bg-gray-200`} style={{ top: "70%" }}></div>
          <div className="absolute bottom-4 right-4 text-[8px] font-bold" style={{ color: accent }}>total —</div>
        </div>
      );
    case "utility":
      return (
        <div className="w-full h-full bg-white p-2 font-mono">
          <div className="text-[6px] font-bold">TAX INVOICE #INV-0001</div>
          <div className="border-b border-dashed border-black my-1"></div>
          <div className="text-[4px]">FROM: Company · TO: Client</div>
          <div className="border-b border-dashed border-black my-1"></div>
          <div className="text-[4px] space-y-0.5">
            <div>Item 1 ..... 100.00</div>
            <div>Item 2 ..... 200.00</div>
          </div>
          <div className="border-b border-dashed border-black my-1"></div>
          <div className="text-[7px] font-bold" style={{ color: accent }}>TOTAL ₹—</div>
        </div>
      );
    case "modern":
      return (
        <div className="w-full h-full bg-white relative">
          <div className="h-1/3 w-full flex items-end p-2" style={{ background: accent }}>
            <div className="text-white text-[7px] font-bold">Tax Invoice</div>
          </div>
          <div className="p-3">
            <div className="text-[5px] text-gray-400 uppercase">Bill To</div>
            <div className="text-[6px] font-semibold">Client</div>
            <div className={`${bar} left-3 right-3 bg-gray-200`} style={{ top: "70%" }}></div>
            <div className={`${bar} left-3 right-3 bg-gray-200`} style={{ top: "78%" }}></div>
            <div className="absolute bottom-3 right-3 text-[7px] font-bold" style={{ color: accent }}>TOTAL —</div>
          </div>
        </div>
      );
    case "corporate":
      return (
        <div className="w-full h-full bg-white p-3">
          <div className="flex items-center justify-between">
            <div className="text-[7px] font-serif font-bold">Company</div>
            <div className="text-[8px] font-serif" style={{ color: accent }}>Tax Invoice</div>
          </div>
          <div className="h-0.5 mt-2" style={{ background: accent }}></div>
          <div className="h-px bg-gray-200 mt-0.5"></div>
          <div className={`${bar} left-3 right-3 bg-gray-200`} style={{ top: "60%" }}></div>
          <div className={`${bar} left-3 right-3 bg-gray-200`} style={{ top: "68%" }}></div>
          <div className="absolute bottom-3 right-3 text-[7px] font-bold" style={{ color: accent }}>TOTAL —</div>
        </div>
      );
    case "minimal":
      return (
        <div className="w-full h-full bg-white p-4">
          <div className="text-[4px] text-gray-400 lowercase">tax invoice</div>
          <div className="mt-2 text-[10px] font-bold" style={{ color: accent }}>INV-0001</div>
          <div className="h-px bg-gray-200 mt-3"></div>
          <div className="mt-3 grid grid-cols-2 gap-1 text-[5px] text-gray-500 lowercase">
            <span>issued by</span><span>billed to</span>
          </div>
          <div className="absolute bottom-4 right-4 text-[7px] font-bold" style={{ color: accent }}>TOTAL —</div>
        </div>
      );
    case "elegant":
      return (
        <div className="w-full h-full p-3 relative" style={{ background: "#FDFCF9" }}>
          <div className="text-center text-[10px] font-serif" style={{ color: accent }}>Tax Invoice</div>
          <div className="h-px mx-8 mt-1" style={{ background: accent }}></div>
          <div className="h-px mx-6 mt-0.5" style={{ background: accent, opacity: 0.5 }}></div>
          <div className="text-center text-[6px] font-serif font-bold mt-2">Company</div>
          <div className="absolute bottom-3 right-3 text-[7px] font-serif font-bold" style={{ color: accent }}>Total —</div>
        </div>
      );
    case "stripe":
      return (
        <div className="w-full h-full bg-white relative">
          <div className="absolute left-0 top-0 bottom-0 w-2" style={{ background: accent }}></div>
          <div className="p-2 pl-4">
            <div className="text-[6px] font-bold">COMPANY</div>
            <div className="mt-2 text-[9px] font-black" style={{ color: accent }}>TAX INVOICE</div>
            <div className={`${bar} left-4 right-3 bg-gray-200`} style={{ top: "65%" }}></div>
            <div className={`${bar} left-4 right-3 bg-gray-200`} style={{ top: "73%" }}></div>
            <div className="absolute bottom-3 right-3 text-[7px] font-bold" style={{ color: accent }}>TOTAL —</div>
          </div>
        </div>
      );
    default:
      return <div className="w-full h-full grid place-items-center text-xs text-gray-400">preview</div>;
  }
}

export default function Landing() {
  const [templates, setTemplates] = useState([]);
  useEffect(() => { fetchTemplates().then(setTemplates).catch(() => {}); }, []);

  // Show 10 distinct base layouts on landing (first of each base)
  const showcase = React.useMemo(() => {
    const byBase = new Map();
    for (const t of templates) {
      if (!byBase.has(t.base)) byBase.set(t.base, t);
    }
    return Array.from(byBase.values());
  }, [templates]);

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--fg)]">
      {/* Nav */}
      <nav className="border-b border-[var(--border)] bg-white/70 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[var(--fg)] text-white grid place-items-center font-bold">IF</div>
            <div className="text-[15px] font-semibold tracking-tight">InvoiceForge</div>
            <span className="text-[10px] text-gray-400 ml-1">by PrismGrim</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/terms" className="text-sm px-4 py-2 hover:bg-gray-100 rounded-md" data-testid="link-terms">Terms</Link>
            <Link to="/login" className="text-sm px-4 py-2 hover:bg-gray-100 rounded-md" data-testid="link-signin">Sign in</Link>
            <Link to="/register" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="link-getstarted">
              Get started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none"></div>
        <div className="max-w-[1200px] mx-auto px-6 pt-24 pb-20 relative">
          <div className="overline mb-6">A billing operating system · Invoices · Receipts · PO · GST</div>
          <h1 className="text-[64px] leading-[0.95] tracking-tight font-medium max-w-4xl">
            Generate <span className="font-serif italic">professional</span> invoices,
            <br /> receipts and POs — for any business.
          </h1>
          <p className="mt-8 text-[17px] text-gray-600 max-w-2xl leading-relaxed">
            Pick from <span className="font-mono text-[var(--fg)]">{templates.length || 30}</span> distinct templates
            with attractive status ribbons (paid, unpaid, overdue, cancelled) — every document stored as a PDF.
            Track GST, totals, paid and unpaid across your entire client base.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/register" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="hero-start">
              Start free — no card <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="text-sm px-5 py-[10px] border border-[var(--border)] rounded-md hover:bg-white" data-testid="hero-signin">
              I already have an account
            </Link>
          </div>

          {/* Doc-type grid */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: FileText, label: "Proforma · Sales · Timesheet" },
              { icon: Receipt, label: "Payment & Expense Receipts" },
              { icon: ClipboardList, label: "Purchase Orders" },
              { icon: Layers, label: "Credit / Debit Notes" },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.label} className="card-flat p-5 hover-lift">
                  <Icon className="w-5 h-5 mb-4 text-[var(--primary)]" />
                  <div className="text-sm font-medium">{f.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Templates showcase */}
      <section className="border-t border-[var(--border)] bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="overline mb-3">{templates.length || 30} PDF templates · 10 distinct layouts</div>
              <h2 className="text-4xl font-medium tracking-tight max-w-2xl">
                Not another purple gradient. <span className="font-serif italic">Actual</span> design.
              </h2>
              <p className="text-sm text-gray-600 mt-3 max-w-xl">
                From formal Swiss and serif Editorial, to Bold Brutalist, Corporate, Elegant, and Minimal —
                each in 3 accent color variants, with your logo, GST breakdown and status ribbon on every PDF.
              </p>
            </div>
            <Link to="/register" className="text-sm underline underline-offset-4" data-testid="templates-cta">Try them all →</Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {(showcase.length ? showcase : Array.from({ length: 10 })).map((t, i) => (
              <div key={t?.id || i} className="card-flat p-3 hover-lift" data-testid={`landing-tpl-${t?.base || i}`}>
                <div className="tpl-preview rounded-sm mb-3 relative overflow-hidden border border-[var(--border)]">
                  {t ? <TplPreview base={t.base} accent={t.accent} /> : <div className="w-full h-full bg-gray-50"></div>}
                </div>
                <div className="text-sm font-medium truncate">{t?.name || "…"}</div>
                <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ background: t?.accent || "#ccc" }}></div>
                  <span className="font-mono">{t?.base || "…"}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-[1200px] mx-auto px-6 text-xs text-gray-500 flex items-center justify-between flex-wrap gap-3">
          <div>© 2026 PrismGrim · InvoiceForge · Built for every kind of business</div>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-[var(--fg)]" data-testid="footer-terms">Terms &amp; Conditions</Link>
            <span className="font-mono">v1.2</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
